import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  ScrollView,
  View,
  Dimensions,
  TouchableOpacity,
  StatusBar,
  Image,
  ActivityIndicator,
  Alert,
} from "react-native";
import Text from "@components/CustomText";
import { useDispatch, useSelector } from "react-redux";
import { numberValidator, lengthValidator } from "@utils/validators";
import analytics from "@services/analytics";
import { actions as nudgeActions } from "@reducers/nudgeReducer";
import AsyncStorage from "@react-native-async-storage/async-storage";
import MapView, { PROVIDER_GOOGLE } from "react-native-maps";
import RoundedBack from "@assets/svg/RoundedBack";
import ChangeMap from "@assets/svg/ChangeMap";
import colors from "@constants/color";
import t from "@constants/localization";
import testIds from "@constants/testIds";
import CustomText from "@components/CustomText";
import { SIGNUP_FLOW_EVENTS } from "../../constants/analyticsEventsCT";
import { actions as authActions } from "../../reducers/authReducer";
import LoadingSreen from "../../components/Loading";
import {
  signUp,
  getNudge,
  getAddressByLatLng,
  getLocationByZipCode,
} from "../../services/api";
import InputBox from "./Components/InputBox";
import isValidLatLong from "../../utils/isValidLatLong";

const { width } = Dimensions.get("screen");
const PATH = "screens/Auth/TLAddress";

export function AddressInput({
  testid,
  label,
  value,
  onChangeText,
  keyboardType = "default",
  error = false,
  errorMessage = "",
  editable = true,
  placeholderHi = "",
  required = false,
  onBlur = () => {},
  returnKeyType = "send",
  bottomLabel = "",
  maxLength,
}) {
  return (
    <View>
      <InputBox
        maxLength={maxLength}
        tId={testid}
        required={required}
        containerStyle={{ paddingVertical: 12 }}
        placeholder={placeholderHi}
        multiline
        value={value}
        onChange={(value) => {
          onChangeText(label, value);
        }}
        inputStyle={{
          paddingLeft: 0,
          marginLeft: 12,
          borderRadius: 0,
        }}
        lines={1}
        borderOnFocus="rgba(0, 0, 0, .1)"
        borderRadius={10}
        fontType="head2"
        keyboardType={keyboardType}
        {...{ error, errorMessage }}
        editable={editable}
        onBlur={onBlur}
        returnKeyType={returnKeyType}
        helperText={bottomLabel}
      />
    </View>
  );
}

export const validationJSON = {
  pincode: {
    min: 6,
    max: 6,
    isNumber: true,
  },
  address1: {
    isRequired: true,
    min: 7,
    max: 100,
    msg: t(`${PATH}/elaborateAdd`),
  },
  address2: {
    isRequired: false,
    min: 7,
    max: 100,
    msg: t(`${PATH}/elaborateAdd`),
  },
  landmark: {
    isRequired: false,
  },
  city: {
    isRequired: true,
  },
};

export const checkSpecialCharacters = (val, key) => {
  if (key === "landmark" && !val) {
    return false;
  }
  if (!val) {
    return;
  }
  let format = /[`•√π÷×¶∆€¥¢°✓™®©!£@#$%^&*()_+\=\[\]{};':"|<>?~]/;
  if (key === "pincode") {
    format = /[` •√π÷×¶∆€¥¢°✓™®©!£@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
  }
  if (key === "city") format = { test: (city) => !(city?.length > 2) };

  const hasSpecial = format.test(val.trim());
  return hasSpecial;
};

const checkForOnlyNumbers = (val, key) => {
  const format = /^\d+$/;
  if (key === "pincode") {
    return false;
  }
  if (!val) {
    return false;
  }
  const hasOnlyNumber = format.test(val.trim());
  return hasOnlyNumber;
};

const formValidator = (validationJson = {}, data = {}) => {
  let isValid = true;
  const errorObj = {};
  const {
    address1 = "",
    address2 = "",
    pincode = "",
    landmark = "",
    city,
  } = data;

  const valueObj = {
    address1,
    address2,
    pincode,
    landmark,
    city,
  };
  Object.keys(valueObj).forEach((v) => {
    if (validationJson[v]) {
      if (checkSpecialCharacters(valueObj[v], v)) {
        isValid = isValid ? false : isValid;
        errorObj[v] = true;
      }
      if (checkForOnlyNumbers(valueObj[v], v)) {
        isValid = isValid ? false : isValid;
        errorObj[v] = true;
      }
      if (validationJson[v].isRequired) {
        if (!valueObj[v] || (valueObj[v] && valueObj[v].trim() === "")) {
          isValid = isValid ? false : isValid;
          errorObj[v] = true;
        }
      }
      if (validationJson[v].isNumber) {
        if (
          !numberValidator(
            valueObj[v],
            validationJson[v].min,
            validationJson[v].max,
          )
        ) {
          isValid = isValid ? false : isValid;
          errorObj[v] = true;
        }
      } else {
        if (validationJson[v].min && validationJson[v].max) {
          if (
            !lengthValidator(
              valueObj[v],
              validationJson[v].min,
              validationJson[v].max,
            )
          ) {
            isValid = isValid ? false : isValid;
            errorObj[v] = true;
          }
        }
      }
    }
  });
  return { isValid, errorObj, allValues: valueObj };
};

function TLAddress({ navigation, route }) {
  const [fields, setFields] = useState({
    address1: "",
    address2: "",
    landmark: "",
    pincode: "",
    city: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [add, setAdd] = useState({});
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [mapType, setMapType] = useState("terrain");
  const [pincodeError, setPincodeError] = useState(null);
  const [isPincodeLoading, setIsPincodeLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector(({ auth }) => auth.user);

  const region = {
    latitude: parseFloat(route.params?.lat || (user && user.lat)),
    longitude: parseFloat(route.params?.lng || (user && user.lng)),
    latitudeDelta: 0.015,
    longitudeDelta: 0.0121,
  };

  const isLatLngValid = isValidLatLong(region.latitude, region.longitude);

  const addressFromLatLng = (lat, lng) => {
    getAddressByLatLng(lat, lng)
      .then((res) => {
        if (res.data.results.length > 0) {
          setAdd({
            address: res.data.results[0].formatted_address,
            pincode: res.data.results[0].pincode,
          });
        }
      })
      .catch((err) => {
        console.log("error", err);
      });
  };

  const onValueChange = (key, value) => {
    setFields((s) => ({
      ...s,
      [key]: value,
    }));
    if (key === "pincode") {
      const { city } = fields;
      setPincodeError(null);
      if (value.length === 6) {
        setIsPincodeLoading(true);
        getLocationByZipCode(value)
          .then(({ data }) => {
            if (data?.city !== city) {
              Alert.alert("", "आपका पिनकोड सिटी से मैच नहीं हो रहा है", [
                { text: "ठीक है", onPress: () => {} },
              ]);
              setPincodeError("कृपया पिन कोड जाँच लें");
            }
          })
          .catch((err) => {
            setPincodeError("कृपया पिन कोड जाँच लें");
          })
          .finally(() => {
            setIsPincodeLoading(false);
          });
      }
    }
    if (key === "address1" && value.length >= 7) {
      setErrors({
        ...errors,
        [key]: false,
      });
    }
    if (key === "address2" && value.length >= 7) {
      setErrors({
        ...errors,
        [key]: false,
      });
    }
  };

  const getNudges = () => {
    getNudge()
      .then(({ data }) => {
        dispatch(nudgeActions.setNudge(data));
      })
      .catch((err) => console.warn(err));
  };

  useEffect(() => {
    const {
      city = "",
      pincode = "",
      address1 = "",
      address2 = "",
    } = user ?? {};
    const { city: cityFromRoute, pincode: pinFromRoute } = route.params || {};
    const finalCity = cityFromRoute || city?.trim();
    const finalPin = pinFromRoute || pincode?.trim();

    if (finalCity && finalPin) {
      setFields((s) => ({
        ...s,
        city: finalCity,
        pincode: finalPin,
        address1: address1 ?? "",
        address2: address2 ?? "",
      }));
    }
    addressFromLatLng(region.latitude, region.longitude);
  }, []);

  const checkAfterSubmit = () => {
    if (firstSubmit) {
      const values = formValidator(validationJSON, fields);
      if (!values.isValid) setErrors(values.errorObj);
      else setErrors({});
    }
  };

  const fromBottomBar = route.params && route.params.fromBottomBar;
  const fromPlaceOrder = route.params && route.params.fromPlaceOrder;
  const fromSupport = route.params && route.params.fromSupport;
  const fromDrawer = route.params && route.params.fromDrawer;
  const fromCiAddressModal = route.params && route.params.fromCiAddressModal;

  const submitAddress = async () => {
    setFirstSubmit(true);

    const values = formValidator(validationJSON, fields);
    const payload = {
      address1: fields.address1
        .replace(/\s+/g, " ")
        .replace(/^\s/, "")
        .replace(/\s$/, "")
        .replace(/^,|,$/g, ""),
      address2: fields.address2
        .replace(/\s+/g, " ")
        .replace(/^\s/, "")
        .replace(/\s$/, "")
        .replace(/^,|,$/g, ""),
      landmark: fields.landmark
        .replace(/\s+/g, " ")
        .replace(/^\s/, "")
        .replace(/\s$/, "")
        .replace(/^,|,$/g, ""),
      pincode: fields.pincode,
      city: fields.city,
    };
    try {
      if (values.isValid) {
        setIsSubmitting(true);
        const { data } = await signUp(payload);
        await AsyncStorage.setItem("address_added", "true");
        let inputSource = "";
        if (fromBottomBar) {
          inputSource = "Nudge";
        } else if (fromPlaceOrder) {
          inputSource = "Checkout";
        } else if (!user.address1) {
          inputSource = "Signup";
        } else if (fromSupport) {
          inputSource = "Support";
        } else if (fromDrawer) {
          inputSource = "Drawer";
        } else if (fromCiAddressModal) {
          inputSource = "payment_flow";
        } else {
          inputSource = "Profile";
        }
        analytics.trackEvent(SIGNUP_FLOW_EVENTS.ADDRESS_SUBMIT, {
          source: route.params?.source,
        });
        setIsSubmitting(false);
        if (fromPlaceOrder) {
          navigation.replace("ConfirmOrder");
        } else if (route.params?.inputSource !== "locationViaAddress") {
          navigation.goBack();
          // navigation.replace('HomeStack');
          // navigation.reset("HomeStack", {
          //   // screen: "LocationViaAddress",
          // });
        } else {
          navigation.replace("HomeStack");
        }
        if (data) {
          getNudges();
          dispatch(authActions.restoreUserToken(true)); // call setup -3 and /me
        }
      } else {
        setErrors(values.errorObj);
        setIsSubmitting(false);
      }
    } catch (e) {
      console.log("error", e);
      setIsSubmitting(false);
      setErrors({});
    }
  };

  const getErrorMessage = (field) => {
    if (errors[field]) {
      if (field === "address2") {
        if (checkSpecialCharacters(fields.address2)) {
          return t(`${PATH}/invalidInput`);
        }
        return validationJSON.address2.msg;
      } else {
        if (checkSpecialCharacters(fields[field])) {
          return t(`${PATH}/invalidInput`);
        }
        return validationJSON[field].msg;
      }
    }
  };

  const handleChangeMapType = () => {
    if (mapType === "terrain") {
      setMapType("satellite");
    }
    if (mapType === "satellite") {
      setMapType("terrain");
    }
  };

  const { address1, pincode, city } = fields;

  const disabled =
    !address1 ||
    !pincode ||
    (pincode && pincode.length !== 6) ||
    !!pincodeError ||
    !city ||
    isPincodeLoading;

  return (
    <>
      <View style={styles.container}>
        <StatusBar barStyle="dark-content" />
        <View style={styles.mapview}>
          <TouchableOpacity
            onPress={() => {
              navigation.goBack();
            }}
            style={styles.roundedback}
          >
            <RoundedBack />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={handleChangeMapType}
            style={styles.changemap}
          >
            <ChangeMap />
          </TouchableOpacity>
          {isLatLngValid ? (
            <>
              <MapView
                provider={PROVIDER_GOOGLE}
                style={{ flex: 1, overflow: "hidden" }}
                minZoomLevel={2}
                maxZoomLevel={20}
                zoomEnabled={false}
                zoomTapEnabled={false}
                pitchEnabled={false}
                gestureHandling={false}
                region={region}
                showsUserLocation={true}
                showsMyLocationButton={false}
                mapType={mapType}
                scrollEnabled={false}
              />

              <View style={styles.mapimgview}>
                <Image
                  resizeMode="contain"
                  style={{ height: 32, width: 32 }}
                  source={require("../../assets/images/you_are_here.webp")}
                />
              </View>
            </>
          ) : (
            <View style={styles.stretchFill}>
              <ActivityIndicator size="small" color={colors.primary} />
            </View>
          )}
        </View>
        <ScrollView stickyHeaderIndices={1} style={styles.scrollview}>
          <View style={styles.sview1}>
            <View style={styles.sview2}>
              <View style={styles.row}>
                <Image
                  resizeMode="contain"
                  style={{ height: 30, width: 30 }}
                  source={require("../../assets/images/you_are_here.webp")}
                />
                <Text numberOfLines={1} style={styles.addresstext}>
                  {add.address}
                </Text>
                <Text
                  onPress={() => {
                    navigation.replace("LocationViaAddress", {
                      fromNotServiceable: true,
                    });
                  }}
                  style={styles.changelocationtext}
                >
                  {t(`${PATH}/changeLocation`)}
                </Text>
              </View>
            </View>
            <View style={{ paddingVertical: 15 }} />
            <AddressInput
              testid={testIds.address1}
              label="address1"
              placeholderHi={t(`${PATH}/address1`)}
              value={fields.address1}
              onChangeText={onValueChange}
              error={errors.address1}
              errorMessage={getErrorMessage("address1")}
              required={true}
              onBlur={checkAfterSubmit}
              bottomLabel={t(`${PATH}/address1Example`)}
            />

            <AddressInput
              testid={testIds.address2}
              label="address2"
              placeholderHi={t(`${PATH}/address2`)}
              value={fields.address2}
              onChangeText={onValueChange}
              error={errors.address2}
              onBlur={checkAfterSubmit}
              errorMessage={getErrorMessage("address2")}
              required={true}
            />
            <AddressInput
              testid={testIds.landmark}
              label="landmark"
              placeholderHi={t(`${PATH}/landmark`)}
              value={fields.landmark}
              onChangeText={onValueChange}
              onBlur={checkAfterSubmit}
              error={errors.landmark}
              errorMessage={getErrorMessage("landmark")}
              required={false}
            />
            <AddressInput
              testid={testIds.pincode}
              label="pincode"
              maxLength={6}
              placeholderHi={t(`${PATH}/pincode`)}
              value={fields.pincode}
              onChangeText={onValueChange}
              keyboardType="numeric"
              // disabled={!fromSignup}
              onBlur={checkAfterSubmit}
              required={true}
              returnKeyType="done"
            />
            <AddressInput
              editable={!fields.city}
              testid={testIds.city}
              label="city"
              placeholderHi={t(`${PATH}/city`)}
              value={fields.city}
              onChangeText={onValueChange}
              disabled={true}
              required={true}
              onBlur={checkAfterSubmit}
            />
          </View>
        </ScrollView>
      </View>
      <View style={styles.btnview}>
        <TouchableOpacity
          disabled={disabled}
          testID={testIds.Save_Address}
          onPress={submitAddress}
          style={[
            styles.btncontainer,
            { backgroundColor: disabled ? "rgba(0, 0, 0, 0.1)" : "#E40046" },
          ]}
        >
          <Text style={styles.btntext}>सेव करें और आगे जाएं </Text>
        </TouchableOpacity>
      </View>
      <LoadingSreen visible={isSubmitting} />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: "relative",
    backgroundColor: "#FFFFFF",
  },
  mapview: {
    height: 160,
    position: "relative",
  },
  roundedback: {
    width: 40,
    height: 40,
    position: "absolute",
    top: 15,
    left: 15,
    zIndex: 1000,
  },
  changemap: {
    width: 34,
    height: 34,
    position: "absolute",
    top: 15,
    right: 15,
    zIndex: 1000,
    backgroundColor: "white",
    borderRadius: 100,
    alignItems: "center",
    justifyContent: "center",
  },
  mapimgview: {
    left: "50%",
    marginLeft: -16,
    marginTop: -32,
    position: "absolute",
    top: "50%",
  },
  scrollview: {
    marginTop: -10,
    borderRadius: 8,
    backgroundColor: "white",
  },
  sview1: {
    marginBottom: 60,
    paddingHorizontal: 15,
  },
  sview2: {
    marginTop: 10,
    marginTop: 20,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },
  addresstext: {
    marginLeft: 4,
    fontSize: 12,
    color: "rgba(0,0,0,0.86)",
    flex: 1,
  },
  changelocationtext: {
    backgroundColor: "#e5e5e5",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
    color: "#E40046",
    fontSize: 12,
    fontWeight: "bold",
  },
  divider: {
    height: 1,
    marginVertical: 12,
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  },
  btnview: {
    position: "absolute",
    width,
    alignItems: "center",
    backgroundColor: "#fff",
    bottom: 0,
    paddingBottom: 20,
  },
  btncontainer: {
    width: width - 30,
    borderRadius: 8,
    backgroundColor: "#E40046",
    paddingVertical: 13,
  },
  btntext: {
    fontFamily: "NotoSansDevanagari-Bold",
    color: "white",
    fontSize: 16,
    lineHeight: 28,
    marginRight: 7,
    textAlign: "center",
  },
  stretchFill: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default TLAddress;
